import type { ValueEvent } from '@adornis/base/utilTypes.js';
import { allTinyMCEPlugins } from '@adornis/writer/x-tinymce.js';
import { LogistikOrder } from '@chancenwerk/db/LogistikOrder.js';
import { ChromiumDialog } from '@chancenwerk/design/components/cr-dialog.js';
import '@chancenwerk/design/components/cr-flex.js';
import '@chancenwerk/design/components/cr-input.js';
import { ChromiumSnackbar } from '@chancenwerk/design/components/cr-snackbar.js';
import { html, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';

const mailerOptions = ['CHANCENlogistik', 'CHANCENhefte'] as const;
type Mailer = (typeof mailerOptions)[number];

@customElement('logistik-dialog-send-customer-email')
export class LogistikDialogSendCustomerEmail extends ChromiumDialog<boolean> {
  static override get element_name() {
    return 'logistik-dialog-send-customer-email';
  }
  @property({ attribute: false }) logistikOrder!: LogistikOrder;

  @state() private _emailContent = '';
  @state() private _emailSubject = '';
  @state() private _mailer: Mailer = 'CHANCENlogistik';

  override heading = 'E-Mail an Kund:in senden';

  override connectedCallback(): void {
    super.connectedCallback();

    this._mailer =
      mailerOptions.find(s => s.toLowerCase() === this.logistikOrder.sourceService?.toLowerCase()) ?? 'CHANCENlogistik';
    this._emailSubject = `Bestellung ${this.logistikOrder.orderNumber}`;
  }

  override content() {
    return html` <cr-flex space="md" padding="md">
      ${this.logistikOrder.emailsSent
        ? html`
            <cr-text bold>
              Bereits ${this.logistikOrder.emailsSent} E-Mail${this.logistikOrder.emailsSent > 1 ? 's' : ''} gesendet
            </cr-text>
          `
        : nothing}
      <cr-dropdown-selection
        placeholder="Ausgangsemail"
        .selectables=${mailerOptions}
        .value=${this._mailer}
        @value-picked=${(e: ValueEvent<Mailer>) => {
          this._mailer = e.detail.value;
        }}
      >
      </cr-dropdown-selection>
      <cr-input
        type="text"
        placeholder="Betreff"
        .value=${this._emailSubject}
        @value-picked=${(e: CustomEvent) => (this._emailSubject = e.detail.value)}
      ></cr-input>
      <x-tinymce
        menubar="false"
        editable
        minheight="50vh"
        placeholder="Inhalt"
        .editorOptions=${{
          convert_urls: false,
          plugins: allTinyMCEPlugins,
        }}
        @value-picked=${(e: CustomEvent) => (this._emailContent = e.detail.value)}
      >
      </x-tinymce>
    </cr-flex>`;
  }

  override footerContent() {
    return html` <cr-flex horizontal crossaxis-center end space="md">
      <cr-button mode="outline" icon="cancel" @click=${() => this.close(false)}> Cancel </cr-button>
      <cr-button
        icon="send"
        @click=${async () => {
          await this.onSend();
        }}
      >
        Send
      </cr-button>
    </cr-flex>`;
  }

  private async onSend() {
    const confirmation = await ChromiumDialog.confirm('Email absenden?');
    if (!confirmation) return;

    const sendResult =
      (await ChromiumDialog.waitFor(
        LogistikOrder.sendCustomEmailToCustomer(
          this.logistikOrder,
          this._emailSubject,
          this._emailContent,
          this._mailer === 'CHANCENhefte',
        )(),
      ).catch(() => false)) ?? false;
    if (sendResult) void ChromiumSnackbar.show('Email erfolgreich versendet');
    else void ChromiumDialog.alert('Fehler beim Senden der Email');
    void this.close(sendResult);
  }
}
